// Core
import { useState, useCallback, useRef } from 'react';
import { HiDotsVertical } from 'react-icons/hi';
import { useHistory, Link, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { FaCheck } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import { GrFormClose } from 'react-icons/gr';
import { 
  Box, 
  Button,
  Flex, 
  Image,
  Grid, 
  Text, 
  Table,
  Tbody,
  Thead,
  Tr,
  Td,
  Skeleton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton
} from '@chakra-ui/react';

// Assets
import { apiService } from 'services/ApiService';
import { validateLoadMore } from 'services/Utils';
import { textLang } from 'services/Lang';
import placeholderUser from 'assets/img/placeholder-user.png';

// Components
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody';

function Liked({
  isLoadingPage,
  setIsLoadingPage,
  onBackListClicked
}) {
  const history = useHistory();
  const { id } = useParams();
  const limit = 20;
  const [list, setList] = useState([]);
  const [listMeta, setListMeta] = useState({total: 0, limit, offset}); 
  const [offset, setOffset] = useState(0);  
  const [isLoadingButton, setIsLoadingButton] = useState(false);

  const style = {
    borderRadius: '40px',
    background: 'white',
    padding: '22px',
    width: '64px',
    height: '64px',
    overflow: 'visible'
  };

  const { isFetching: isFetchingList } = useQuery({
    queryKey: ['list', offset],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const params = {
        limit, 
        offset,
        id
      };

      const response = await apiService.getGroupLiked(params);
      setIsLoadingPage(false);
      if(offset === 0){
          setList(response.data);
      } else {
          setList([...list, ...response.data]);
      }

      setListMeta(response.meta);
      return true;
    },
    retry: false
  });
 
  const loadMore = async () => {
    setOffset(offset + limit);
  };

  const [processingIds, setProcessingIds] = useState(new Set());
  const markProcessing = (id) => {
    setProcessingIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const handleApprove = useCallback(async (item) => {
    markProcessing(item.id);

    try {
      await apiService.approvedToGroup({
        groupId: id,
        id: item.user.id,
      });
    } catch (e) {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  }, []);

  const handleRejected = useCallback(async (item) => {
    markProcessing(item.id);

    try {
      await apiService.rejectedFromGroup({
        groupId: id,
        id: item.owner,
      });
    } catch (e) {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  }, []);


  return (
    <>
      <Box overflow="hidden">
        <Box>
          <Grid templateColumns={{ base: '1fr' }} gap="24px" templateRows='1fr'>
            {list?.length > 0 &&
              list.map((data) => {
                const isProcessing = processingIds.has(data.id);

                return (
                  <Flex
                    key={data.id}
                    gap="8px"
                    justifyContent="space-between"
                    opacity={isProcessing ? 0.3 : 1}
                    pointerEvents={isProcessing ? 'none' : 'auto'}
                    transition="opacity 0.2s ease"
                  >
                    <Flex gap="8px" alignItems="center">
                      <Image
                        src={data?.user?.image ?? placeholderUser}
                        borderRadius="100%"
                        w="64px"
                        h="64px"
                        objectFit="cover"
                      />
                      <Text variant="title-lg">{data?.user?.id ? data?.user?.firstName : 'DELETED'}</Text>
                    </Flex>

                    <Flex gap="8px">
                      <Box
                        position="relative"
                        style={style}
                        p="13px !important"
                        onClick={() => handleRejected(data)}
                      >
                        <GrFormClose fontSize="40px" />
                      </Box>

                      { data?.user?.id &&
                        <Box
                          position="relative"
                          style={style}
                          onClick={() => handleApprove(data)}
                        >
                          <FaCheck fontSize="21px" />
                        </Box>
                      }
                    </Flex>
                  </Flex>
                );
              })}

            { list?.length === 0 && !isFetchingList &&
              <Box
                width="100%"
                height="100vh"
                textAlign="center"
                position="relative">
                <Box 
                  w="100%" 
                  bottom="80px" 
                  h="40vh" 
                  overflow="hidden" 
                  position="fixed"
                  zIndex="3"
                >
                  <Text px="10%" variant="title-lg" fontSize="24px">
                  {textLang('pay_note_3')}
                  </Text>
                </Box>
              </Box>
            }
          </Grid>
        </Box>

        { list?.length === 0 && isFetchingList && 
          <Box p="5px">
            { Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} h="64px" borderRadius="24px" mb="5px" />
            ))}
          </Box>
        }

        { validateLoadMore(list, listMeta) && (
          <Button variant="solid-lg" onClick={loadMore} m="16px auto" display="block">
            {textLang('show_more')}
          </Button>
        )}
      </Box>

      <Box textAlign="center" zIndex="999" bg="#ffffff" position="fixed" bottom="0px" w="100%" h="124px" left="0" p="8px 15px">
        <Flex gap="8px">
          <Button onClick={onBackListClicked}  variant="solid-lg" style={{width: '140px'}}>{textLang('back')}</Button>
        </Flex>
      </Box>  
    </>
  );
}

export default Liked;